/**
 * iOS Status Bar Manager
 * 
 * This utility provides functions to control the iOS native status bar color
 * from React/PWA through a JavaScript bridge to Swift.
 */

/**
 * Check if the app is running in iOS native wrapper
 * @returns {boolean} True if running in iOS native app
 */
export const isIOSNativeApp = () => {
  return window.webkit && 
         window.webkit.messageHandlers && 
         window.webkit.messageHandlers.setStatusBarColor !== undefined;
};

/**
 * Set the iOS status bar color
 * @param {string} hexColor - Hex color string (e.g., "#FF5733" or "FF5733")
 * @returns {boolean} True if the message was sent successfully
 */
export const setIOSStatusBarColor = (hexColor) => {
  if (!isIOSNativeApp()) {
    console.log('[iOS Status Bar] Not running in iOS native app, skipping');
    return false;
  }

  try {
    // Clean the hex color if needed
    let cleanColor = hexColor.trim();
    if (!cleanColor.startsWith('#')) {
      cleanColor = '#' + cleanColor;
    }

    // Send message to native iOS
    window.webkit.messageHandlers.setStatusBarColor.postMessage({
      color: cleanColor
    });

    console.log(`[iOS Status Bar] Color changed to: ${cleanColor}`);
    return true;
  } catch (error) {
    console.error('[iOS Status Bar] Error setting color:', error);
    return false;
  }
};

/**
 * Reset the iOS status bar to default (white background, dark content)
 * @returns {boolean} True if the message was sent successfully
 */
export const resetIOSStatusBarColor = () => {
  return setIOSStatusBarColor('#FFFFFF');
};

/**
 * Set status bar color based on a CSS color value
 * This converts CSS color names to hex values
 * @param {string} cssColor - CSS color (e.g., "red", "#FF0000", "rgb(255,0,0)")
 * @returns {boolean} True if the message was sent successfully
 */
export const setIOSStatusBarColorFromCSS = (cssColor) => {
  if (!isIOSNativeApp()) {
    return false;
  }

  try {
    // Create a temporary element to compute the color
    const tempDiv = document.createElement('div');
    tempDiv.style.color = cssColor;
    document.body.appendChild(tempDiv);
    
    const computedColor = window.getComputedStyle(tempDiv).color;
    document.body.removeChild(tempDiv);

    // Extract RGB values
    const rgbMatch = computedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]);
      const g = parseInt(rgbMatch[2]);
      const b = parseInt(rgbMatch[3]);
      
      // Convert to hex
      const hexColor = '#' + 
        r.toString(16).padStart(2, '0') + 
        g.toString(16).padStart(2, '0') + 
        b.toString(16).padStart(2, '0');
      
      return setIOSStatusBarColor(hexColor);
    }
    
    // If it's already a hex color, use it directly
    if (cssColor.startsWith('#')) {
      return setIOSStatusBarColor(cssColor);
    }

    console.warn('[iOS Status Bar] Could not parse color:', cssColor);
    return false;
  } catch (error) {
    console.error('[iOS Status Bar] Error converting CSS color:', error);
    return false;
  }
};

/**
 * Hook to automatically set status bar color on component mount
 * Usage in React component:
 * 
 * useEffect(() => {
 *   setIOSStatusBarColor('#FF5733');
 *   return () => resetIOSStatusBarColor();
 * }, []);
 */

export default {
  isIOSNativeApp,
  setIOSStatusBarColor,
  resetIOSStatusBarColor,
  setIOSStatusBarColorFromCSS
};
